import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Typography from '../components/Text/Typography';
import TextInput from '../components/Text/TextInput';
import Button from '../components/Button/Button';
import Chip from '../components/Chip/Chip';
import ModalDialog from '../components/Dialog/ModalDialog';
import PageShell from '../components/PageShell/PageShell';
import Surface from '../components/Surface/Surface';
import { API_BASE_URL, authenticatedFetch } from '../config/api';
import './Settings.css';

const CONCERNS = [
  'Breakouts & Blemishes',
  'Tone & Pigmentation',
  'Texture & Smoothness',
  'Moisture & Hydration',
  'Oil Control',
  'Aging & Elasticity',
  'Sensitivity & Irritation',
];

const CONDITIONS = [
  'Acne',
  'Eczema',
  'Rosacea',
  'Psoriasis',
  'Hyperpigmentation / Melasma',
  'Sensitive / Reactive Skin',
  'Fungal Acne (Malassezia Folliculitis)',
  'Keratosis Pilaris',
  'Other',
];

function formatBirthdayForInput(value: string | Date | null | undefined): string {
  if (!value) return '';
  try {
    let dateStr = typeof value === 'string' ? value : value instanceof Date ? value.toISOString().split('T')[0] : '';
    if (dateStr.includes('T')) dateStr = dateStr.split('T')[0];
    const [y, m, d] = dateStr.split('-');
    if (y && m && d) return `${m}/${d}/${y}`;
  } catch (_) {}
  return '';
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser, signOut } = useAuth();
  const { showToast } = useToast();
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [selectedConcerns, setSelectedConcerns] = useState<string[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Load profile from auth context
  useEffect(() => {
    if (user) {
      setName(user.name ?? '');
      setDob(formatBirthdayForInput(user.birthday));
      const concerns = user.skin_concerns;
      setSelectedConcerns(Array.isArray(concerns) ? concerns : []);
      const conditions = user.skin_conditions;
      setSelectedConditions(Array.isArray(conditions) ? conditions : []);
    }
  }, [user]);

  const toggleConcern = (concern: string) => {
    setSelectedConcerns((sel) =>
      sel.includes(concern) ? sel.filter((c) => c !== concern) : [...sel, concern]
    );
  };

  const toggleCondition = (condition: string) => {
    setSelectedConditions((sel) =>
      sel.includes(condition) ? sel.filter((c) => c !== condition) : [...sel, condition]
    );
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/user/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          onboardingCompleted: user.onboarding_completed ?? true,
          name,
          dob,
          skinConcerns: selectedConcerns,
          skinConditions: selectedConditions,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save');
      }
      await refreshUser();
      showToast('Profile saved successfully', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save profile';
      setSaveError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/user/account`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete account');
      }
      await signOut();
      showToast('Account deleted', 'info');
      navigate('/', { replace: true });
      window.location.href = '/';
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete account';
      setDeleteError(message);
      showToast(message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteConfirmText('');
    setDeleteError(null);
    setDeleteConfirmOpen(true);
  };

  const canDelete = deleteConfirmText === 'DELETE';

  if (!user) {
    return (
      <PageShell width="narrow" className="settings-page">
        <Typography variant="body">Loading profile...</Typography>
      </PageShell>
    );
  }

  return (
    <PageShell width="narrow" className="settings-page">
      <div className="settings-container">
        <Typography variant="h1" className="settings-title">
          Profile &amp; Settings
        </Typography>

        <Surface variant="section" padding="md" className="settings-section" as="section">
          <Typography variant="h3" className="settings-section-title">
            Profile information
          </Typography>
          <div className="settings-form">
            <div className="settings-field">
              <Typography variant="body" className="settings-label">
                Name
              </Typography>
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </div>
            <div className="settings-field">
              <Typography variant="body" className="settings-label">
                Date of birth
              </Typography>
              <TextInput
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                placeholder="MM/DD/YYYY"
              />
            </div>
            <div className="settings-field">
              <Typography variant="body" className="settings-label">
                Skin concerns
              </Typography>
              <div className="settings-chips">
                {CONCERNS.map((c) => (
                  <Chip
                    key={c}
                    label={c}
                    isSelected={selectedConcerns.includes(c)}
                    onClick={() => toggleConcern(c)}
                  />
                ))}
              </div>
            </div>
            <div className="settings-field">
              <Typography variant="body" className="settings-label">
                Skin conditions
              </Typography>
              <div className="settings-chips">
                {CONDITIONS.map((c) => (
                  <Chip
                    key={c}
                    label={c}
                    isSelected={selectedConditions.includes(c)}
                    onClick={() => toggleCondition(c)}
                  />
                ))}
              </div>
            </div>
            {saveError && (
              <Typography variant="body-small" className="settings-error">
                {saveError}
              </Typography>
            )}
            <div className="settings-actions">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </Surface>

        <Surface variant="section" padding="md" className="settings-section settings-danger-zone" as="section">
          <Typography variant="h3" className="settings-section-title">
            Danger zone
          </Typography>
          <Typography variant="body" className="settings-danger-text">
            Deleting your account is permanent. All your data, routines, and cabinet will be removed and cannot be recovered.
          </Typography>
          <Button variant="secondary" onClick={openDeleteModal} className="settings-delete-btn">
            Delete account
          </Button>
        </Surface>
      </div>

      <ModalDialog
        isOpen={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        title="Delete account"
        maxWidth="400px"
        primaryButton={{
          label: deleting ? 'Deleting…' : 'Delete my account',
          onClick: handleDeleteAccount,
          disabled: !canDelete || deleting,
        }}
        secondaryButton={{
          label: 'Cancel',
          onClick: () => setDeleteConfirmOpen(false),
          disabled: deleting,
        }}
      >
        <div className="settings-delete-modal">
          <Typography variant="body">
            This action cannot be undone. All your data will be permanently removed from our servers and your Cognito account will be deleted.
          </Typography>
          <Typography variant="body" className="settings-delete-instruction">
            Type <strong>DELETE</strong> below to confirm.
          </Typography>
          <TextInput
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE"
            className="settings-delete-input"
          />
          {deleteError && (
            <Typography variant="body-small" className="settings-error">
              {deleteError}
            </Typography>
          )}
        </div>
      </ModalDialog>
    </PageShell>
  );
};

export default Settings;
